// C:\Users\server\Desktop\FRONTEND\app-user-front\src\pages\Contactus.js
import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, Alert, Spinner } from "react-bootstrap";
import CONFIG from "../config";

// ---------------- Helper: compute API base like Productadd ----------------
const computeApiBase = () => {
  const rawBase = (CONFIG?.API_URL || "").replace(/\/+$/, "") || "";
  if (!rawBase) return "";
  if (/\/api$/i.test(rawBase)) return rawBase; // already ends with /api
  return `${rawBase}/api`;
};

// Final API endpoint -> https://demo.swapiify.com/api/contactus
const API_BASE = computeApiBase();
const POST_URL = API_BASE ? `${API_BASE}/contactus` : "";

export default function Contactus() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail]         = useState("");
  const [phone, setPhone]         = useState("");
  const [subject, setSubject]     = useState("");
  const [message, setMessage]     = useState("");
  const [loading, setLoading]     = useState(false);
  const [feedback, setFeedback]   = useState({ type: "", text: "" });

  useEffect(() => {
    if (feedback.type === "success") {
      const t = setTimeout(() => setFeedback({ type: "", text: "" }), 6000);
      return () => clearTimeout(t);
    }
  }, [feedback.type]);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setSubject("");
    setMessage("");
  };

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setFeedback({ type: "", text: "" });

    if (!API_BASE || !POST_URL) {
      setFeedback({
        type: "danger",
        text: "API base URL is not configured.",
      });
      return;
    }

    // Frontend validation
    if (!firstName || !lastName || !email || !subject || !message) {
      setFeedback({
        type: "danger",
        text: "Please fill required fields: First, Last, Email, Subject, Message.",
      });
      return;
    }

    if (!isValidEmail(email)) {
      setFeedback({
        type: "danger",
        text: "Please enter a valid email address.",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: `${firstName} ${lastName}`,
        email,
        phone,
        subject,
        message,
      };

      console.log("Contact POST_URL:", POST_URL);
      console.log("Contact payload:", payload);

      const res = await fetch(POST_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log("Contact response status:", res.status);

      if (res.ok) {
        setFeedback({
          type: "success",
          text: "Thanks — your message has been sent.",
        });
        resetForm();
      } else {
        let errText = "This service is unavailable.";
        try {
          const json = await res.json();
          console.log("Contact error JSON:", json);
          if (json?.message) errText = json.message;
          if (json?.error) errText = json.error;
        } catch (e) {
          const text = await res.text();
          console.log("Contact raw error text:", text);
        }
        setFeedback({ type: "danger", text: errText });
      }
    } catch (error) {
      console.error("Contact network error:", error);
      setFeedback({
        type: "danger",
        text: "This service is unavailable.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const styles = {
    root: {
      fontFamily:
        'Inter, "Segoe UI", Roboto, system-ui, -apple-system, sans-serif',
      color: "#222",
      background: "#fff",
    },
    hero: {
      background: "#6a2d57",
      padding: "115px 0",
      textAlign: "center",
    },
    heroTitle: {
      color: "#ff8b5a",
      fontFamily: "'League Spartan', sans-serif",
      fontSize: 48,
      margin: 0,
      fontWeight: 700,
    },
    container: { maxWidth: 1150, margin: "0 auto", padding: "40px 16px" },
    introTitle: {
      fontFamily: "'League Spartan', sans-serif",
      fontSize: 22,
      marginBottom: 12,
      color: "#1f1f1f",
    },
    introText: { color: "#555", lineHeight: 1.6, marginBottom: 20 },
    infoRow: {
      display: "flex",
      gap: 30,
      alignItems: "flex-start",
      marginTop: 20,
    },
    infoBox: { display: "flex", gap: 12, alignItems: "flex-start" },
    infoIcon: {
      width: 44,
      height: 44,
      borderRadius: 10,
      border: "2px solid #222",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
    },
    cardWrap: { display: "flex", justifyContent: "flex-end" },
    formCard: {
      width: "520px",
      background: "#fff",
      borderRadius: 12,
      padding: 24,
      boxShadow: "0 8px 18px rgba(0,0,0,0.12)",
      border: "1px solid rgba(0,0,0,0.04)",
    },
    formTitle: { fontSize: 20, marginBottom: 8, fontWeight: 700 },
    formText: { color: "#666", fontSize: 14, marginBottom: 12 },
    purpleBtn: { background: "#6a2d57", borderColor: "#6a2d57", color: "#fff" },
    footerNote: { color: "#666", marginTop: 30 },
  };

  return (
    <div style={styles.root}>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=League+Spartan:wght@700&display=swap');
          @media (max-width: 767px) {
            .contact-two-col { flex-direction: column !important; }
            .contact-left { padding-right: 0 !important; margin-bottom: 24px; }
            .form-card { width: 100% !important; }
          }
        `}
      </style>

      {/* HERO */}
      <section style={styles.hero} aria-label="Contact hero">
        <h1 style={styles.heroTitle}>Contact Us</h1>
      </section>

      {/* MAIN */}
      <div style={styles.container}>
        <div
          style={{ display: "flex", gap: 24, alignItems: "flex-start" }}
          className="contact-two-col"
        >
          {/* LEFT */}
          <div style={{ flex: 1 }} className="contact-left">
            <h3 style={styles.introTitle}>We Are Ready To Help</h3>
            <p style={styles.introText}>
              Have questions, need assistance, or want to know more about
              bartering on our platform? Our team is here to help! Reach out to
              us anytime, and we'll get back to you as soon as possible.
            </p>

            <div style={styles.infoRow}>
              <div style={styles.infoBox}>
                <div style={styles.infoIcon} aria-hidden>
                  <svg
                    width="20"
                    height="14"
                    viewBox="0 0 20 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1.2H19V12.8H1V1.2Z"
                      stroke="#222"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 2.2L10 8.2L19 2.2"
                      stroke="#222"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <h5 style={{ margin: 0 }}>Email Address</h5>
                  <div style={{ color: "#666", marginTop: 6 }}>
                    info@swapiify.com
                  </div>
                </div>
              </div>

              <div style={styles.infoBox}>{/* extra info box space */}</div>
            </div>
          </div>

          {/* RIGHT (form card) */}
          <div style={styles.cardWrap}>
            <div
              style={styles.formCard}
              className="form-card"
              role="region"
              aria-labelledby="send-message-title"
            >
              <h4 id="send-message-title" style={styles.formTitle}>
                Send us a message
              </h4>
              <p style={styles.formText}>
                Have questions or need assistance? We’re here to help! Reach out
                for support, inquiries, or feedback, and we'll get back to you
                as soon as possible.
              </p>

              {feedback.text && (
                <Alert
                  variant={feedback.type === "success" ? "success" : "danger"}
                  onClose={() => setFeedback({ type: "", text: "" })}
                  dismissible
                >
                  {feedback.text}
                </Alert>
              )}

              <Form onSubmit={handleSubmit} noValidate>
                <Row>
                  <Col sm={6}>
                    <Form.Group className="mb-3" controlId="firstName">
                      <Form.Control
                        placeholder="First Name *"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group className="mb-3" controlId="lastName">
                      <Form.Control
                        placeholder="Last Name *"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col sm={6}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Control
                        type="email"
                        placeholder="Your Email *"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group className="mb-3" controlId="phone">
                      <Form.Control
                        placeholder="Your Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="subject">
                  <Form.Control
                    placeholder="Subject *"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="message">
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="Message *"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </Form.Group>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <Button
                    type="submit"
                    style={styles.purpleBtn}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" /> Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={resetForm}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>

        <div style={styles.footerNote}>
          <hr />
          <p style={{ marginTop: 12 }}>
            Swap smarter, live sustainably. Turn what you have into what you
            need with effortless exchanges and a thriving community.
          </p>
        </div>
      </div>
    </div>
  );
}
